/**
 * Shared normalize → clamp for drag-resize bounds and optional initial size.
 * Used by `useDragResize1D` and `ResizableDrawer` first-paint `sx` seeding.
 */

function normalizeMinMax(min: number, max: number): [number, number] {
  if (min > max) {
    if (import.meta.env?.DEV) {
      console.warn("[useDragResize1D] min > max; clamping max to min", {
        min,
        max,
      });
    }
    return [min, min];
  }
  return [min, max];
}

function clampSize(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

function sanitizeSize(
  next: number,
  min: number,
  max: number,
  fallback: number
): number {
  const clamped = clampSize(next, min, max);
  if (!Number.isFinite(clamped)) {
    return Number.isFinite(fallback) ? fallback : min;
  }
  return clamped;
}

export type ResolveDragResizeSizeInput = {
  min: number;
  max: number;
  /** When omitted, result has no `initialSize` (no placeholder seed). */
  initialSize?: number;
  /** Fallback when clamped initial is non-finite; defaults to normalized `min`. */
  fallback?: number;
};

export type ResolveDragResizeSizeResult = {
  min: number;
  max: number;
  initialSize?: number;
};

/**
 * Normalize `min`/`max` (incl. `min > max` → `[min, min]`), then clamp optional `initialSize`.
 */
export function resolveDragResizeSize(
  input: ResolveDragResizeSizeInput
): ResolveDragResizeSizeResult {
  const [lo, hi] = normalizeMinMax(input.min, input.max);
  if (input.initialSize === undefined) {
    return { min: lo, max: hi };
  }
  const fallback = input.fallback ?? lo;
  return {
    min: lo,
    max: hi,
    initialSize: sanitizeSize(input.initialSize, lo, hi, fallback),
  };
}

/** Clamp an arbitrary size into normalized bounds (drag / setSize). */
export function clampDragResizeSize(
  next: number,
  min: number,
  max: number,
  fallback: number
): number {
  const [lo, hi] = normalizeMinMax(min, max);
  return sanitizeSize(next, lo, hi, fallback);
}
